// client/src/user/dashboard/chat/MarkdownRenderer.js
import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Box, Typography, Divider, useTheme, alpha } from "@mui/material";
import CodeBlock from "./CodeBlock";

const MarkdownRenderer = ({ content }) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        minWidth: 0,
        maxWidth: "100%",
        overflowX: "hidden",
        wordBreak: "break-word",
        overflowWrap: "anywhere",
        "& > :first-of-type": {
          mt: 0,
        },
        "& > :last-child": {
          mb: 0,
        },
      }}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          code({ node, inline, className, children, ...props }) {
            const match = /language-(\w+)/.exec(className || "");
            const codeValue = String(children).replace(/\n$/, "");

            if (!inline && match) {
              return <CodeBlock language={match[1]} value={codeValue} />;
            }

            return (
              <Box
                component="code"
                sx={{
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  borderRadius: "5px",
                  color: "primary.main",
                  px: 0.75,
                  py: 0.2,
                  fontFamily: '"JetBrains Mono", "Fira Code", monospace',
                  fontSize: "0.82em",
                  fontWeight: 500,
                  wordBreak: "break-all",
                  whiteSpace: "pre-wrap",
                }}
                {...props}
              >
                {children}
              </Box>
            );
          },

          pre({ children }) {
            return (
              <Box
                sx={{
                  maxWidth: "100%",
                  minWidth: 0,
                  overflow: "visible",
                }}
              >
                {children}
              </Box>
            );
          },

          p({ children }) {
            return (
              <Typography
                component="p"
                sx={{
                  fontSize: { xs: "0.9rem", sm: "0.9375rem" },
                  lineHeight: 1.75,
                  color: "text.primary",
                  mb: 1.25,
                  "&:last-child": { mb: 0 },
                  wordBreak: "break-word",
                  overflowWrap: "anywhere",
                }}
              >
                {children}
              </Typography>
            );
          },

          h1({ children }) {
            return (
              <Typography
                variant="h5"
                fontWeight={800}
                sx={{
                  mt: 2.5,
                  mb: 1,
                  color: "text.primary",
                  wordBreak: "break-word",
                }}
              >
                {children}
              </Typography>
            );
          },

          h2({ children }) {
            return (
              <Typography
                variant="h6"
                fontWeight={700}
                sx={{
                  mt: 2,
                  mb: 0.75,
                  color: "text.primary",
                  wordBreak: "break-word",
                }}
              >
                {children}
              </Typography>
            );
          },

          h3({ children }) {
            return (
              <Typography
                sx={{
                  fontSize: "1rem",
                  fontWeight: 700,
                  mt: 1.5,
                  mb: 0.5,
                  color: "text.primary",
                  wordBreak: "break-word",
                }}
              >
                {children}
              </Typography>
            );
          },

          ul({ children }) {
            return (
              <Box
                component="ul"
                sx={{
                  pl: { xs: 2.25, sm: 2.5 },
                  my: 1,
                  "& li": { mb: 0.4 },
                }}
              >
                {children}
              </Box>
            );
          },

          ol({ children }) {
            return (
              <Box
                component="ol"
                sx={{
                  pl: { xs: 2.25, sm: 2.5 },
                  my: 1,
                  "& li": { mb: 0.4 },
                }}
              >
                {children}
              </Box>
            );
          },

          li({ children }) {
            return (
              <Box
                component="li"
                sx={{
                  fontSize: { xs: "0.9rem", sm: "0.9375rem" },
                  lineHeight: 1.7,
                  color: "text.primary",
                  wordBreak: "break-word",
                  overflowWrap: "anywhere",
                }}
              >
                {children}
              </Box>
            );
          },

          blockquote({ children }) {
            return (
              <Box
                component="blockquote"
                sx={{
                  borderLeft: `3px solid ${theme.palette.primary.main}`,
                  pl: 2,
                  py: 0.5,
                  my: 1.5,
                  mx: 0,
                  bgcolor: alpha(theme.palette.primary.main, 0.05),
                  borderRadius: "0 8px 8px 0",
                  "& p": {
                    mb: 0,
                    color: "text.secondary",
                    fontStyle: "italic",
                  },
                }}
              >
                {children}
              </Box>
            );
          },

          hr() {
            return <Divider sx={{ my: 2 }} />;
          },

          strong({ children }) {
            return (
              <Box
                component="strong"
                sx={{ fontWeight: 700, color: "text.primary" }}
              >
                {children}
              </Box>
            );
          },

          em({ children }) {
            return (
              <Box
                component="em"
                sx={{ fontStyle: "italic", color: "text.secondary" }}
              >
                {children}
              </Box>
            );
          },

          a({ href, children }) {
            return (
              <Box
                component="a"
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: "primary.main",
                  textDecoration: "underline",
                  textDecorationColor: alpha(theme.palette.primary.main, 0.4),
                  wordBreak: "break-all",
                  "&:hover": {
                    textDecorationColor: theme.palette.primary.main,
                  },
                }}
              >
                {children}
              </Box>
            );
          },

          table({ children }) {
            return (
              <Box
                sx={{
                  overflowX: "auto",
                  my: 2,
                  maxWidth: "100%",
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 2,
                  "&::-webkit-scrollbar": { height: 5 },
                  "&::-webkit-scrollbar-thumb": {
                    bgcolor: alpha(theme.palette.text.primary, 0.18),
                    borderRadius: 2,
                  },
                }}
              >
                <Box
                  component="table"
                  sx={{
                    width: "100%",
                    minWidth: { xs: 520, sm: "max-content" },
                    borderCollapse: "collapse",
                    fontSize: "0.875rem",
                    "& th, & td": {
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      borderRight: `1px solid ${theme.palette.divider}`,
                      px: 1.5,
                      py: 0.75,
                      textAlign: "left",
                      whiteSpace: "nowrap",
                    },
                    "& th:last-of-type, & td:last-of-type": {
                      borderRight: "none",
                    },
                    "& tr:last-of-type td": {
                      borderBottom: "none",
                    },
                    "& th": {
                      bgcolor: alpha(theme.palette.primary.main, 0.07),
                      fontWeight: 700,
                      color: "text.primary",
                    },
                    "& tr:nth-of-type(even)": {
                      bgcolor: alpha(theme.palette.text.primary, 0.025),
                    },
                  }}
                >
                  {children}
                </Box>
              </Box>
            );
          },
        }}
      >
        {content}
      </ReactMarkdown>
    </Box>
  );
};

export default MarkdownRenderer;
